import React, { useState } from 'react';
import moment from 'moment';
import pluralize from 'pluralize';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { getRequiredMarketingPartnerListingJobs } from 'common/marketingPartnerJobs/getRequiredMarketingPartnerListingJobs';

import {
  JOB_ORDER_STATUS_ACCEPTED,
  JOB_ORDER_STATUS_ASSIGNED,
  JOB_ORDER_STATUS_CANCELLED,
  JOB_ORDER_STATUS_COMPLETED,
  JOB_ORDER_STATUS_CREATED,
  JOB_ORDER_STATUS_FAILED,
  JOB_STATUS_CANCELLED,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_FAILED,
  JOB_TO_ICON_MAPPING,
  srJobTypeTextMapping,
} from 'common/jobConstants';

import {
  DATA_TABLE_SOURCE_GOING_TO_MARKET_V2_LISTINGS,
  DATA_TABLE_VIEW_ID_GOING_TO_MARKET_V2_LISTINGS_ALL,
  DATA_TABLE_VIEW_ID_GOING_TO_MARKET_V2_LISTINGS_TEMP_OFF,
} from 'common/dataTableConstants';

import {
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
} from 'common/marketingPartnerListingConstants';
import SrText from 'client/components/SrText';

const DATA_TABLE_VIEW_ID_TO_FILTERS_MAPPING = {
  [DATA_TABLE_VIEW_ID_GOING_TO_MARKET_V2_LISTINGS_ALL]: {
    'marketingPartnerListings.status': [
      MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
      MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
      MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
    ],
  },
  [DATA_TABLE_VIEW_ID_GOING_TO_MARKET_V2_LISTINGS_TEMP_OFF]: {
    'marketingPartnerListings.status': [MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET],
  },
};

const useSrGoingToMarketV2DataTableController = () => {
  const [activeViewID, setActiveViewID] = useState(
    DATA_TABLE_VIEW_ID_GOING_TO_MARKET_V2_LISTINGS_ALL,
  );
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [jobOrder, setJobOrder] = useState(null);
  const [listingId, setListingId] = useState(null);

  function getDataTableSettingsFromProps() {
    const dataTableSettings = {
      dataSource: DATA_TABLE_SOURCE_GOING_TO_MARKET_V2_LISTINGS,
      rootViewId: activeViewID,
      viewId: activeViewID,
      initialViewSettings: {
        filter: DATA_TABLE_VIEW_ID_TO_FILTERS_MAPPING[activeViewID],
        loadAllData: false,
      },
      itemsPropName: 'listings',
      count: 25,
    };
    return dataTableSettings;
  }

  const _getHeaders = () => {
    return [
      {
        content: 'Physical Req.',
        id: 'physicalRequirements',
      },
      {
        content: 'Promise',
        id: 'promiseDate',
      },
      {
        content: 'Due',
        id: 'dueDate',
      },
      {
        content: 'Address',
        id: 'addressStreetDisplay',
      },
      {
        content: 'Market',
        id: 'marketDisplayName',
      },
      {
        content: 'Assigned/Accepted',
        id: 'assignedAcceptedStatus',
      },
      {
        content: 'Completed',
        id: 'completedStatus',
      },
      {
        content: 'Notes',
        id: 'notesCount',
      },
    ];
  };

  const formatDate = (date) => {
    return date ? moment(date).format('MMM D') : 'N/A';
  };

  const _getPhysicalRequirementsColor = ({ targetJobTypeId, jobOrders }) => {
    const jobFound = (jobOrders || [])
      .flatMap((jobOrder) => jobOrder.jobs)
      .find((job) => job.jobTypeId === targetJobTypeId);

    if (!jobFound) {
      return 'red';
    }

    switch (jobFound.status) {
      case JOB_STATUS_FAILED:
      case JOB_STATUS_CANCELLED:
        return 'red';
      case JOB_STATUS_COMPLETED:
        return 'green';
      default:
        return 'black';
    }
  };

  const getPhysicalRequirements = (listing) => {
    const requiredMarketingPartnerListingJobs = getRequiredMarketingPartnerListingJobs({
      listing: {
        services: listing.services,
        occupantType: listing.occupantType,
        propertySetupAccessType: listing.propertySetupAccessType,
      },
      preLease: listing.preLease,
      partner: listing.partner || {},
      marketingPartnerListing: {
        requireSunlock: listing.requireSunlock,
        yardSignAllowed: listing.yardSignAllowed,
        requireSelfShowingHardware: listing.requireSelfShowingHardware,
        requirePropertyConditionReport: listing.requirePropertyConditionReport,
        requirePhotos: listing.requirePhotos,
        requireVirtual3dTour: listing.requireVirtual3dTour,
      },
    });

    const physicalRequirements = requiredMarketingPartnerListingJobs.map((jobTypeId) => ({
      id: jobTypeId,
      icon: JOB_TO_ICON_MAPPING[jobTypeId],
      color: _getPhysicalRequirementsColor({
        targetJobTypeId: jobTypeId,
        jobOrders: listing.jobOrders || [],
      }),
      text: srJobTypeTextMapping[jobTypeId],
    }));

    return physicalRequirements;
  };

  const getDueDateUpdatesText = ({ dueDateUpdates }) => {
    return dueDateUpdates.map((dueDateUpdate) => {
      return (
        <SrText key={dueDateUpdate.id} fontSize="13px" lineHeight="13px">
          {dueDateUpdate.userName || 'API'} {' on '}
          {momentWithAustinTimezone(dueDateUpdate.createdDate).format('MMM D, h:mma')}:&nbsp; From{' '}
          {momentWithAustinTimezone(dueDateUpdate.oldValue).format('MMM D')}&nbsp; to{' '}
          {momentWithAustinTimezone(dueDateUpdate.newValue).format('MMM D')}
          <br />
        </SrText>
      );
    });
  };

  const getPromiseDateColor = ({ promiseDate }) => {
    const today = moment().startOf('day');
    const promiseDateMoment = moment(promiseDate).startOf('day');
    if (promiseDateMoment.isBefore(today)) {
      return 'red';
    }
    return 'green';
  };

  const getDueDateColor = ({ dueDate, jobOrderStatus }) => {
    const today = moment().startOf('day');
    const dueDateMoment = moment(dueDate).startOf('day');

    if (jobOrderStatus === JOB_ORDER_STATUS_COMPLETED) {
      return 'green';
    }
    if (jobOrderStatus === JOB_ORDER_STATUS_CANCELLED) {
      return 'grey-crossed-out';
    }
    if (dueDateMoment.isBefore(today)) {
      return 'red';
    }
    if (dueDateMoment.isSame(today)) {
      return 'yellow';
    }
    return 'black';
  };

  const _getJobStatusColor = ({ jobStatus }) => {
    switch (jobStatus) {
      case JOB_STATUS_FAILED:
        return 'red';
      case JOB_STATUS_COMPLETED:
        return 'green';
      case JOB_ORDER_STATUS_CANCELLED:
        return 'grey';
      default:
        return 'black';
    }
  };

  const getJobIcons = ({ jobs }) => {
    return jobs.map(({ id, jobTypeId, status }) => ({
      id,
      icon: JOB_TO_ICON_MAPPING[jobTypeId],
      color: _getJobStatusColor({ jobStatus: status }),
      text: srJobTypeTextMapping[jobTypeId],
    }));
  };

  const getJobOrderStatusColor = ({ jobOrderStatus }) => {
    switch (jobOrderStatus) {
      case JOB_ORDER_STATUS_FAILED:
      case JOB_ORDER_STATUS_ASSIGNED:
        return 'red';
      case JOB_ORDER_STATUS_ACCEPTED:
      case JOB_ORDER_STATUS_COMPLETED:
        return 'green';
      case JOB_ORDER_STATUS_CANCELLED:
        return 'grey-crossed-out';
      default:
        return 'black';
    }
  };

  const getJobOrderStatus = ({ status, driverName }) => {
    switch (status) {
      case JOB_ORDER_STATUS_ACCEPTED:
      case JOB_ORDER_STATUS_ASSIGNED:
      case JOB_ORDER_STATUS_COMPLETED:
        return driverName || 'N/A';
      case JOB_ORDER_STATUS_CREATED:
        return 'Needs Assigning';
      default:
        return status;
    }
  };

  const onNotesModalClose = () => {
    setIsNotesModalOpen(false);
  };

  const onNotesClick = ({ jobOrderId, listingId }) => {
    setJobOrder(jobOrderId);
    setListingId(listingId);
    setIsNotesModalOpen(true);
  };

  const getNotesCount = ({ notesCount }) => {
    return pluralize('Note', notesCount, true);
  };

  return {
    headers: _getHeaders(),
    getDataTableSettingsFromProps,
    formatDate,
    getNotesCount,
    getJobOrderStatus,
    getJobOrderStatusColor,
    setActiveViewID,
    getPromiseDateColor,
    getDueDateColor,
    getDueDateUpdatesText,
    getJobIcons,
    onNotesModalClose,
    onNotesClick,
    getPhysicalRequirements,
    isNotesModalOpen,
    selectedJobOrderId: jobOrder,
    selectedListingId: listingId,
  };
};

export default useSrGoingToMarketV2DataTableController;
